import { useEffect, useState } from "react";
import { DollarSign, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const NumField = ({ label, value, onChange, prefix = "$", suffix = "", testid }) => (
  <label className="block">
    <span className="text-xs text-zinc-500">{label}</span>
    <div className="mt-1 flex items-center bg-black/40 border border-white/10 rounded-lg px-3 focus-within:border-[#007AFF]/50">
      {prefix && <span className="text-zinc-500 text-sm">{prefix}</span>}
      <input
        data-testid={testid}
        type="number"
        min="0"
        step="0.01"
        value={value}
        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
        className="w-full bg-transparent px-1.5 py-2 text-sm text-white focus:outline-none"
      />
      {suffix && <span className="text-zinc-500 text-sm">{suffix}</span>}
    </div>
  </label>
);

export default function PricingSettingsPanel() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/pricing-settings").then((r) => setCfg(r.data.settings)).catch(() => {});
  }, []);

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const setNested = (group, k, v) => setCfg((c) => ({ ...c, [group]: { ...c[group], [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put("/admin/pricing-settings", {
        markup_pct: Number(cfg.markup_pct) || 0,
        api_cost_per_fix: Number(cfg.api_cost_per_fix) || 0,
        fix_labor: {
          small: Number(cfg.fix_labor.small) || 0,
          medium: Number(cfg.fix_labor.medium) || 0,
          large: Number(cfg.fix_labor.large) || 0,
        },
        hosting_fee: {
          monitor: Number(cfg.hosting_fee.monitor) || 0,
          pro: Number(cfg.hosting_fee.pro) || 0,
          agency: Number(cfg.hosting_fee.agency) || 0,
        },
      });
      setCfg(res.data.settings);
      toast.success("Pricing saved");
    } catch { toast.error("Could not save pricing"); }
    finally { setSaving(false); }
  };

  if (!cfg) return null;

  return (
    <div className="bg-[#121212] border border-white/10 rounded-xl p-5 mt-6" data-testid="pricing-settings-panel">
      <div className="flex items-center gap-2 mb-1">
        <DollarSign size={16} className="text-[#32D74B]" />
        <h3 className="font-heading font-semibold text-white text-sm uppercase tracking-wide">Pricing settings</h3>
      </div>
      <p className="text-zinc-500 text-xs mb-5">These are placeholders until you set your real numbers. Estimates = (labor + API cost) + markup. Hosting fees apply only to managed plans and default to $0 (no live price change).</p>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <NumField label="Markup on services + API" testid="ps-markup" prefix="" suffix="%" value={cfg.markup_pct} onChange={(v) => set("markup_pct", v)} />
        <NumField label="API cost per fix" testid="ps-api-cost" value={cfg.api_cost_per_fix} onChange={(v) => set("api_cost_per_fix", v)} />
      </div>

      <div className="mt-5">
        <p className="text-xs font-bold uppercase tracking-wide text-zinc-500 mb-2">Done-for-you fix labor (by scope)</p>
        <div className="grid grid-cols-3 gap-4">
          <NumField label="🟢 Small" testid="ps-labor-small" value={cfg.fix_labor.small} onChange={(v) => setNested("fix_labor", "small", v)} />
          <NumField label="🟡 Medium" testid="ps-labor-medium" value={cfg.fix_labor.medium} onChange={(v) => setNested("fix_labor", "medium", v)} />
          <NumField label="🔴 Large" testid="ps-labor-large" value={cfg.fix_labor.large} onChange={(v) => setNested("fix_labor", "large", v)} />
        </div>
      </div>

      <div className="mt-5">
        <p className="text-xs font-bold uppercase tracking-wide text-zinc-500 mb-2">Managed hosting fee (added to plan / mo)</p>
        <div className="grid grid-cols-3 gap-4">
          <NumField label="Monitor" testid="ps-hosting-monitor" suffix="/mo" value={cfg.hosting_fee.monitor} onChange={(v) => setNested("hosting_fee", "monitor", v)} />
          <NumField label="Pro" testid="ps-hosting-pro" suffix="/mo" value={cfg.hosting_fee.pro} onChange={(v) => setNested("hosting_fee", "pro", v)} />
          <NumField label="Agency" testid="ps-hosting-agency" suffix="/mo" value={cfg.hosting_fee.agency} onChange={(v) => setNested("hosting_fee", "agency", v)} />
        </div>
      </div>

      <button data-testid="ps-save" onClick={save} disabled={saving} className="mt-5 btn-brand text-white rounded-lg px-5 py-2.5 text-sm font-medium inline-flex items-center gap-2 disabled:opacity-60">
        {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save pricing
      </button>
    </div>
  );
}
